import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { IconDashboard, IconUsers, IconBell, IconLogout, IconPackage, IconWrench, IconSettings, IconCalculator } from '../components/Icons';
import CustomersList from './admin/CustomersList';
import CustomerDetail from './admin/CustomerDetail';
import SiteMap from './admin/SiteMap';
import GateDetail from './admin/GateDetail';
import AlertsList from './admin/AlertsList';
import ProductsList from './admin/ProductsList';
import ProductDetail from './admin/ProductDetail';
import CategoriesList from './admin/CategoriesList';
import TeamPage from './admin/TeamPage';
import SettingsPage from './admin/SettingsPage';
import PricingCalculator from './admin/PricingCalculator';
import GateGroupsPage, { GateGroupDetailPage } from './shared/GateGroupsPage';
import SystemClock from '../components/SystemClock';

function AdminDashboard({ user, setUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [alertCount, setAlertCount] = useState(0);

  useEffect(() => {
    axios.get('/api/admin/alerts')
      .then(res => {
        const unread = res.data.filter(a => !a.is_read).length;
        setAlertCount(unread);
      })
      .catch(() => {});
  }, [location]);

  const handleLogout = async () => {
    await axios.post('/api/auth/logout');
    setUser(null);
  };

  const menuItems = [
    { key: '/', label: 'דשבורד', icon: <IconDashboard className="icon" /> },
    { key: '/customers', label: 'לקוחות', icon: <IconUsers className="icon" /> },
    { key: '/products', label: 'מוצרים', icon: <IconPackage className="icon" /> },
    { key: '/pricing', label: 'תמחור', icon: <IconCalculator className="icon" /> },
    { key: '/team', label: 'צוות חורי', icon: <IconWrench className="icon" /> },
    { key: '/alerts', label: 'התראות', icon: <IconBell className="icon" /> },
    { key: '/settings', label: 'הגדרות', icon: <IconSettings className="icon" /> },
  ];

  const currentPath = location.pathname;

  const isActive = (key) => {
    if (key === '/') return currentPath === '/';
    // Categories live inside the Products world, so keep the Products item
    // highlighted while the admin is managing them.
    if (key === '/products') {
      return currentPath.startsWith('/products') || currentPath.startsWith('/categories');
    }
    return currentPath === key || currentPath.startsWith(key + '/');
  };

  return (
    <div className="app-layout">
      <nav className="sidebar">
        <div className="sidebar-logo">
          <img src="/logo-icon.svg" alt="Huri-Connect" className="sidebar-logo-img" />
          <div className="sidebar-logo-text">
            <h2>Huri-Connect</h2>
            <span>ניהול ראשי</span>
          </div>
        </div>
        <ul className="sidebar-menu">
          {menuItems.map(item => (
            <li
              key={item.key}
              className={isActive(item.key) ? 'active' : ''}
              onClick={() => navigate(item.key)}
            >
              {item.icon} {item.label}
            </li>
          ))}
          <li className="logout" onClick={handleLogout}>
            <IconLogout className="icon" /> יציאה
          </li>
        </ul>
      </nav>
      <div className="main-content">
        <div className="header">
          <h1>{user.name}</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {/* System clock — visible only in the super-admin layout
                (this dashboard). Lets the operator verify the
                server's view of "now" before debugging schedules. */}
            <SystemClock />
            <div className="bell" onClick={() => navigate('/alerts')}>
              <IconBell size={20} />
              {alertCount > 0 && <span className="bell-badge">{alertCount}</span>}
            </div>
          </div>
        </div>
        <div className="page-content">
          <Routes>
            <Route path="/" element={<DashboardHome />} />
            <Route path="/customers" element={<CustomersList />} />
            <Route path="/customers/:id" element={<CustomerDetail />} />
            <Route path="/customers/:id/users/:userId" element={<CustomerDetail />} />
            <Route path="/sites/:id" element={<SiteMap />} />
            <Route path="/sites/:id/groups" element={<GateGroupsPage apiBase="admin" />} />
            <Route path="/sites/:id/groups/:gid" element={<GateGroupDetailPage apiBase="admin" />} />
            <Route path="/gates/:id" element={<GateDetail />} />
            <Route path="/alerts" element={<AlertsList />} />
            <Route path="/products" element={<ProductsList />} />
            <Route path="/products/:id" element={<ProductDetail />} />
            <Route path="/categories" element={<CategoriesList />} />
            <Route path="/pricing" element={<PricingCalculator />} />
            <Route path="/team" element={<TeamPage currentUserId={user.id} />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

function DashboardHome() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get('/api/admin/dashboard').then(res => setData(res.data));
  }, []);

  if (!data) return <div className="loading">טוען...</div>;

  return (
    <>
      <div className="stats-row">
        <div className="stat-card">
          <div className="number">{data.total_gates}</div>
          <div className="label">סה"כ שערים</div>
        </div>
        <div className="stat-card green">
          <div className="number">{data.total_gates - data.open_gates}</div>
          <div className="label">סגורים</div>
        </div>
        <div className="stat-card yellow">
          <div className="number">{data.open_gates}</div>
          <div className="label">פתוחים</div>
        </div>
        <div className="stat-card">
          <div className="number">{data.customers.length}</div>
          <div className="label">לקוחות</div>
        </div>
      </div>

      <div className="data-table">
        <table>
          <thead>
            <tr>
              <th>לקוח</th>
              <th>אתרים</th>
              <th>שערים</th>
              <th>סטטוס</th>
            </tr>
          </thead>
          <tbody>
            {data.customers.map(c => (
              <tr key={c.id} onClick={() => navigate(`/customers/${c.id}`)}>
                <td>{c.name}</td>
                <td>{c.sites_count}</td>
                <td>{c.gates_count}</td>
                <td><span className={`status-dot ${c.status}`}></span></td>
              </tr>
            ))}
            {data.customers.length === 0 && (
              <tr><td colSpan="4" style={{ textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>אין לקוחות עדיין</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default AdminDashboard;
